// OAuth session management for identity service
export interface AuthSession {
  clientId: string;
  redirectUri: string;
  responseType: string;
  scope: string;
  state?: string;
  nonce?: string;
  sessionId?: string;
  originUrl: string;
  timestamp: number;
}

const SESSION_KEY = 'auth_session';
const SESSION_DURATION = 30 * 60 * 1000; // 30 minutes

export function createAuthSession(params: URLSearchParams): AuthSession | null {
  const sessionId = params.get('session_id');
  
  // If session_id is provided, create a minimal session object
  // The actual OAuth parameters are stored server-side
  if (sessionId) {
    const session: AuthSession = {
      clientId: 'session-based',  // Placeholder since actual value is server-side
      redirectUri: 'session-based',  // Placeholder since actual value is server-side
      responseType: 'code',
      scope: 'openid email profile',
      state: undefined,
      nonce: undefined,
      sessionId,
      originUrl: window.location.origin,
      timestamp: Date.now()
    };
    
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    return session;
  }
  
  // Traditional OAuth flow with explicit parameters
  const clientId = params.get('client_id');
  const redirectUri = params.get('redirect_uri');
  const responseType = params.get('response_type') || 'code';
  const scope = params.get('scope') || 'openid email profile';
  const state = params.get('state') || undefined;
  const nonce = params.get('nonce') || undefined;
  
  if (!clientId || !redirectUri) {
    return null;
  }
  
  const session: AuthSession = {
    clientId,
    redirectUri,
    responseType,
    scope,
    state,
    nonce,
    sessionId: undefined,
    originUrl: window.location.origin,
    timestamp: Date.now()
  };
  
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  return session;
}

export function getAuthSession(): AuthSession | null {
  const sessionStr = sessionStorage.getItem(SESSION_KEY);
  if (!sessionStr) {
    return null;
  }
  
  try {
    const session = JSON.parse(sessionStr) as AuthSession;
    
    // Check if session is expired
    if (Date.now() - session.timestamp > SESSION_DURATION) {
      clearAuthSession();
      return null;
    }
    
    return session;
  } catch {
    return null;
  }
}

export function clearAuthSession(): void {
  sessionStorage.removeItem(SESSION_KEY);
}

export function validateOrigin(session: AuthSession, currentOrigin: string): boolean {
  // The origin stored in session should match the current origin
  // This prevents session hijacking from different origins
  return session.originUrl === currentOrigin;
}